import { PayPalScriptProvider, PayPalButtons, FUNDING } from '@paypal/react-paypal-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { get, post } from '~/lib/api';
import { queryKeys } from '~/lib/query-keys';
import type { BillingStatus, PayPalConfig } from '~/types/billing';

interface Props {
  organizationId?: string;
  planId: string;
  paypalPlanId: string;
  paypalConfig: PayPalConfig;
  disabled?: boolean;
  onActivated?: () => void;
  /** When set, uses the public checkout endpoint (no auth required). */
  publicCheckout?: { email: string };
}

type ProcessingState = 'idle' | 'polling' | 'activated' | 'timeout' | 'check_email';

const POLL_INTERVAL = 2000;
const POLL_MAX_DURATION = 30000;

export function PayPalCheckoutButton({
  organizationId,
  planId,
  paypalConfig,
  disabled,
  onActivated,
  publicCheckout,
}: Props) {
  const queryClient = useQueryClient();
  const [processing, setProcessing] = useState<ProcessingState>('idle');
  const pollTimer = useRef<ReturnType<typeof setInterval>>();
  const pollStart = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = undefined;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback(() => {
    setProcessing('polling');
    pollStart.current = Date.now();

    pollTimer.current = setInterval(async () => {
      try {
        const status = await get<BillingStatus>(
          `/billing/status?organizationId=${organizationId}`,
        );
        if (status.subscriptionStatus === 'active') {
          stopPolling();
          setProcessing('activated');
          queryClient.invalidateQueries({
            queryKey: queryKeys.billing.status(organizationId!),
          });
          toast.success('Subscription activated!');
          onActivated?.();
        } else if (Date.now() - pollStart.current > POLL_MAX_DURATION) {
          stopPolling();
          setProcessing('timeout');
        }
      } catch {
        if (Date.now() - pollStart.current > POLL_MAX_DURATION) {
          stopPolling();
          setProcessing('timeout');
        }
      }
    }, POLL_INTERVAL);
  }, [organizationId, queryClient, stopPolling, onActivated]);

  // Authenticated flow
  const createSubMutation = useMutation({
    mutationFn: () =>
      post<{ subscriptionId: string }>('/billing/create-subscription', {
        organizationId,
        planId,
      }),
  });

  // Public flow
  const publicCheckoutMutation = useMutation({
    mutationFn: (email: string) =>
      post<{ subscriptionId: string }>('/billing/public-checkout', {
        email,
        planId,
      }),
  });

  if (processing === 'check_email') {
    return (
      <div className="flex flex-col items-center gap-4 rounded-xl border border-creative-200 bg-creative-50/50 p-8">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-creative-100">
          <Mail className="h-7 w-7 text-creative-600" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900">
            Check your email!
          </p>
          <p className="mt-1 text-sm text-slate-500">
            We&apos;ve sent a link to <strong>{publicCheckout?.email}</strong> to
            complete your account setup.
          </p>
        </div>
      </div>
    );
  }

  if (processing === 'polling') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-creative-200 bg-creative-50/50 p-6">
        <Loader2 className="h-8 w-8 animate-spin text-creative-500" />
        <p className="text-sm font-semibold text-slate-900">
          Processing your payment...
        </p>
        <p className="text-xs text-slate-500">
          This usually takes a few seconds.
        </p>
      </div>
    );
  }

  if (processing === 'activated') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-6">
        <CheckCircle className="h-8 w-8 text-emerald-500" />
        <p className="text-sm font-semibold text-slate-900">
          You&apos;re all set!
        </p>
      </div>
    );
  }

  if (processing === 'timeout') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-6">
        <p className="text-sm font-semibold text-slate-900">
          Payment received!
        </p>
        <p className="text-xs text-slate-500">
          Your account is being set up. You can continue now.
        </p>
        <button
          onClick={() => onActivated?.()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:brightness-110"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const buttonProps = {
    disabled,
    createSubscription: async () => {
      if (publicCheckout) {
        const result = await publicCheckoutMutation.mutateAsync(
          publicCheckout.email,
        );
        return result.subscriptionId;
      }
      const result = await createSubMutation.mutateAsync();
      return result.subscriptionId;
    },
    onApprove: async () => {
      if (publicCheckout) {
        // Public flow: show "check your email" instead of polling
        setProcessing('check_email');
      } else {
        startPolling();
      }
    },
    onCancel: () => {
      toast.info('Checkout canceled — no changes were made.');
    },
    onError: (err: Record<string, unknown>) => {
      console.error('PayPal error:', err);
      toast.error('Payment failed. Please try again.');
    },
  } as const;

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalConfig.clientId,
        vault: true,
        intent: 'subscription',
        enableFunding: 'card',
        disableFunding: 'credit,paylater,venmo',
      }}
    >
      <PayPalButtons
        fundingSource={FUNDING.CARD}
        style={{ shape: 'rect', color: 'black', layout: 'vertical' }}
        {...buttonProps}
      />
    </PayPalScriptProvider>
  );
}
